export interface PollOptions {
  intervalMs: number
  timeoutMs: number
  /** Checked before each wait and again right after — lets a caller (e.g. a
   * component's onBeforeUnmount) stop the loop early. Defaults to never
   * cancelling. */
  isCancelled?: () => boolean
}

// Shared shape for "queue something, then poll until it shows up" flows
// (rule generation, rule improvement, skill import): wait, check, repeat
// until `check` returns a defined result, the caller cancels, or the
// deadline passes. A thrown error from `check` is treated as a transient
// fetch failure and swallowed — polling continues until the deadline.
export async function pollUntil<T>(
  check: () => Promise<T | undefined>,
  { intervalMs, timeoutMs, isCancelled = () => false }: PollOptions,
): Promise<T | undefined> {
  const deadline = Date.now() + timeoutMs

  while (!isCancelled() && Date.now() < deadline) {
    await new Promise<void>((resolve) => setTimeout(resolve, intervalMs))
    if (isCancelled()) return undefined

    try {
      const result = await check()
      if (result !== undefined) return result
    } catch {
      // Transient fetch failure — keep polling until the deadline.
    }
  }

  return undefined
}
